/* ============================================================================
   bsfn-cart-drawer.js — behaviour for sections/bsfn-cart-drawer.liquid

   ORIGIN     ORIGINAL — authored for BSFN against the Shopify platform APIs
              cited below.

   REFERENCE  Cart AJAX API      shopify.dev/docs/api/ajax/reference/cart
              Section Rendering  shopify.dev/docs/api/section-rendering
              Recommendations    shopify.dev/docs/api/ajax/reference/product-recommendations
              Standard events    shopify.dev/docs/api/storefront-events-and-actions/events/dispatch
              Modal dialog a11y  w3.org/WAI/ARIA/apg/patterns/dialog-modal/

   DESIGN NOTES
     · Markup lives in Liquid, never here. After every mutation the drawer asks
       Shopify to re-render its own section and swaps the result in. There is no
       JS copy of the item template to drift out of sync.
     · All handlers are delegated from the root element, so a swapped-in DOM
       needs no rebinding.
     · Every network path has a fallback that navigates to /cart. A failed fetch
       must never leave a customer unable to buy.

   PUBLIC API   window.BSFN.cart.open() | .close() | .refresh() | .add(id, qty)
   EVENTS       listens for  'bsfn:cart:updated'  (dispatched by bsfn-line.js,
                             and by bsfn-agent.js with source 'agent')
                emits        'bsfn:drawer:opened' / 'bsfn:drawer:closed', and —
                             through BSFN.std — Shopify's standard cart events
                             for the line, note and add changes made here
   ========================================================================== */
(function () {
  'use strict';
  /* Storefront root from Shopify (a locale or market prefix lives here), never a bare '/'. */
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  var root = document.querySelector('[data-bsfn-cart-drawer]');
  if (!root || root.__bsfnCartDrawer) return;
  root.__bsfnCartDrawer = true;

  var BSFN = (window.BSFN = window.BSFN || {});
  var dialog = root.querySelector('.bsfn-cd');
  var sectionId = root.getAttribute('data-section-id');
  var openOnAdd = root.getAttribute('data-open-on-add') === 'true';
  var REDUCED = false;
  try { REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}

  var lastFocus = null;
  var qtyTimers = {};
  var noteTimer = null;
  var recsLoaded = false;
  var isOpen = false;
  var hideTimer = null;   // pending "display:none" after the close transition

  /* ---------- small helpers ---------------------------------------------- */

  function inner() { return root.querySelector('[data-cd-inner]'); }
  function status(msg) {
    var el = root.querySelector('[data-cd-status]');
    if (el) el.textContent = msg;
  }
  function focusables() {
    return Array.prototype.filter.call(
      dialog.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),textarea,select,summary,[tabindex]:not([tabindex="-1"])'),
      function (el) { return el.offsetParent !== null; }
    );
  }
  function scrollbarWidth() { return window.innerWidth - document.documentElement.clientWidth; }

  /* ---------- open / close ---------------------------------------------- */

  function open() {
    if (isOpen) return;
    isOpen = true;
    lastFocus = document.activeElement;

    // Cancel any hide still pending from a close that has not finished
    // transitioning. Without this, closing and re-opening inside the 360ms
    // window lets the stale timer fire and set display:none on a drawer that is
    // supposed to be open — leaving an invisible cart and a locked page scroll.
    clearTimeout(hideTimer);
    hideTimer = null;

    dialog.hidden = false;
    var pad = scrollbarWidth();
    document.documentElement.style.overflow = 'hidden';
    if (pad > 0) document.documentElement.style.paddingRight = pad + 'px';

    // Force a reflow so the newly un-hidden dialog has a layout to transition
    // FROM, then flip the class synchronously. Deliberately NOT
    // requestAnimationFrame: a backgrounded or power-throttled tab may never run
    // the callback, which would strand the drawer off-screen with the page
    // scroll already locked — the customer could neither see the cart nor scroll.
    void dialog.offsetWidth;
    root.classList.add('is-open');

    var first = focusables()[0];
    (first || dialog).focus({ preventScroll: true });

    document.addEventListener('keydown', onKeydown, true);
    loadRecs();
    emit('bsfn:drawer:opened');
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;
    root.classList.remove('is-open');
    document.removeEventListener('keydown', onKeydown, true);
    document.documentElement.style.overflow = '';
    document.documentElement.style.paddingRight = '';

    clearTimeout(hideTimer);
    var finish = function () { dialog.hidden = true; hideTimer = null; };
    if (REDUCED) { finish(); } else { hideTimer = setTimeout(finish, 360); }

    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    lastFocus = null;
    emit('bsfn:drawer:closed');
  }

  function onKeydown(e) {
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key !== 'Tab') return;
    var f = focusables();
    if (!f.length) { e.preventDefault(); return; }
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function emit(name, detail) {
    try { document.dispatchEvent(new CustomEvent(name, { detail: detail || {} })); } catch (e) {}
  }

  /* ---------- re-render via the Section Rendering API -------------------- */

  function applySections(payload) {
    if (!payload || !payload.sections || !payload.sections[sectionId]) return false;
    var doc = new DOMParser().parseFromString(payload.sections[sectionId], 'text/html');
    var fresh = doc.querySelector('[data-cd-inner]');
    var target = inner();
    if (!fresh || !target) return false;

    var scroll = root.querySelector('[data-cd-scroll]');
    var y = scroll ? scroll.scrollTop : 0;

    target.innerHTML = fresh.innerHTML;

    var newScroll = root.querySelector('[data-cd-scroll]');
    if (newScroll && y) newScroll.scrollTop = y;

    recsLoaded = false;
    loadRecs();
    subDates();
    return true;
  }

  /* "Next delivery <date>" on subscription lines is computed client-side by
     bsfn-subscribe.js from the selling plan's name; re-run it after every swap. */
  function subDates() {
    if (BSFN.sub && BSFN.sub.dates) BSFN.sub.dates(root);
  }

  function syncCount(count) {
    if (typeof count !== 'number') return;
    root.setAttribute('data-cart-count', count);
    document.querySelectorAll('[data-bsfn-cart-count]').forEach(function (b) {
      b.textContent = count;
      if (count > 0) b.classList.remove('is-empty'); else b.classList.add('is-empty');
      if (!REDUCED && count > 0) {
        b.classList.remove('bsfn-bump');
        void b.offsetWidth;
        b.classList.add('bsfn-bump');
      }
    });
  }

  function post(url, body) {
    body.sections = sectionId;
    body.sections_url = window.location.pathname;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error(url + ' -> ' + r.status);
      return r.json();
    });
  }

  /* Full refresh without mutating anything — used after an external add. */
  function refresh() {
    return fetch(ROOT + '?sections=' + encodeURIComponent(sectionId), { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        applySections({ sections: data });
        return fetch(ROOT + 'cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); });
      })
      .then(function (cart) { syncCount(cart.item_count); return cart; })
      .catch(function () { /* leave the drawer as it stands */ });
  }

  /* ---------- line mutations -------------------------------------------- */

  /* Standard-event handle for a change made here; a no-op until bsfn-agent.js
     has loaded. Every path through a mutation settles it with done or fail. */
  function std(kind, a, b) {
    var S = BSFN.std;
    if (!S) return { done: function () {}, fail: function () {} };
    return kind === 'note' ? S.cartNote(root, 'dialog', a) : S.cartLines(root, kind, 'dialog', a);
  }

  function changeLine(li, quantity) {
    var line = li.getAttribute('data-cd-line');
    li.classList.add('is-busy');
    clearTimeout(qtyTimers[line]);

    qtyTimers[line] = setTimeout(function () {
      var ev = std(quantity === 0 ? 'remove' : 'update', [{ id: li.getAttribute('data-key'), quantity: quantity }]);
      post(ROOT + 'cart/change.js', { line: parseInt(line, 10), quantity: quantity })
        .then(function (cart) {
          ev.done(cart);
          syncCount(cart.item_count);
          applySections(cart);
          status(quantity === 0 ? 'Item removed' : 'Cart updated');
        })
        .catch(function (err) { ev.fail(err); window.location.href = ROOT + 'cart'; });
    }, 260);
  }

  function updateNote(value) {
    clearTimeout(noteTimer);
    noteTimer = setTimeout(function () {
      var ev = std('note', value);
      fetch(ROOT + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ note: value })
      })
        .then(function (r) { if (!r.ok) throw new Error('note -> ' + r.status); return r.json(); })
        .then(function (cart) { ev.done(cart); })
        .catch(function (err) { ev.fail(err); });
    }, 600);
  }

  function add(id, qty, btn) {
    if (btn) { btn.disabled = true; btn.textContent = '…'; }
    var ev = std('add', [{ merchandiseId: id, quantity: qty || 1 }]);
    return post(ROOT + 'cart/add.js', { id: id, quantity: qty || 1 })
      .then(function (res) {
        applySections(res);
        return fetch(ROOT + 'cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        ev.done(cart);
        syncCount(cart.item_count);
        status('Added to cart');
        if (!isOpen && openOnAdd) open();
        emit('bsfn:cart:updated', { cart: cart, source: 'drawer' });
        return cart;
      })
      .catch(function (err) {
        ev.fail(err);
        if (btn) { btn.disabled = false; btn.textContent = '+'; }
        window.location.href = ROOT + 'cart';
      });
  }

  /* ---------- recommendations ------------------------------------------- */

  function money(cents, format) {
    var value = (cents / 100);
    var fmt = format || '${{amount}}';
    function withDelims(num, dec) {
      var parts = num.toFixed(dec).split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
      return parts.join('.');
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, token) {
      switch (token) {
        case 'amount_no_decimals':               return withDelims(value, 0);
        case 'amount_with_comma_separator':      return withDelims(value, 2).replace(/[,.]/g, function (c) { return c === ',' ? '.' : ','; });
        case 'amount_no_decimals_with_comma_separator': return withDelims(value, 0).replace(/,/g, '.');
        default:                                 return withDelims(value, 2);
      }
    });
  }

  function loadRecs() {
    var box = root.querySelector('[data-cd-recs]');
    if (!box || recsLoaded) return;
    var pid = box.getAttribute('data-product-id');
    if (!pid) return;
    recsLoaded = true;

    var url = ROOT + 'recommendations/products.json?product_id=' + encodeURIComponent(pid) +
              '&limit=' + encodeURIComponent(box.getAttribute('data-limit') || 3) +
              '&intent=' + encodeURIComponent(box.getAttribute('data-intent') || 'complementary');

    fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var list = root.querySelector('[data-cd-recs-list]');
        var current = root.querySelector('[data-cd-recs]');
        if (!list || !current) return;

        // don't suggest something already in the cart
        var inCart = {};
        root.querySelectorAll('[data-cd-line] .bsfn-cd__name').forEach(function (a) {
          inCart[a.textContent.trim()] = true;
        });

        var fmt = current.getAttribute('data-money-format');
        var items = (data.products || []).filter(function (p) {
          return p.available && !inCart[p.title.trim()];
        });
        if (!items.length) { current.hidden = true; return; }

        list.innerHTML = items.map(function (p) {
          var v = (p.variants || []).filter(function (x) { return x.available; })[0];
          if (!v) return '';
          var img = p.featured_image || (p.images && p.images[0]) || '';
          return '<li class="bsfn-cd__rec">' +
                   (img ? '<img src="' + img + '" alt="" width="48" height="48" loading="lazy">' : '<img alt="" width="48" height="48">') +
                   '<span class="bsfn-cd__rec-t">' +
                     '<a class="bsfn-cd__rec-n" href="' + p.url + '">' + p.title + '</a>' +
                     '<span class="bsfn-cd__rec-p">' + money(v.price, fmt) + '</span>' +
                   '</span>' +
                   '<button type="button" class="bsfn-cd__rec-add" data-cd-rec-add="' + v.id + '" ' +
                     'aria-label="Add ' + p.title.replace(/"/g, '&quot;') + ' to cart">+</button>' +
                 '</li>';
        }).join('');
        current.hidden = false;
      })
      .catch(function () {
        var box2 = root.querySelector('[data-cd-recs]');
        if (box2) box2.hidden = true;
      });
  }

  /* ---------- delegated events ------------------------------------------ */

  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-cd-close]')) { e.preventDefault(); close(); return; }

    var li = e.target.closest('[data-cd-line]');

    if (e.target.closest('[data-cd-inc]') && li) {
      var upIn = li.querySelector('[data-cd-input]');
      var max = upIn.getAttribute('max');
      var next = parseInt(upIn.value, 10) + 1;
      if (max && next > parseInt(max, 10)) {
        status('That is all we have in stock');
        return;
      }
      upIn.value = next;
      changeLine(li, next);
      return;
    }
    if (e.target.closest('[data-cd-dec]') && li) {
      var dnIn = li.querySelector('[data-cd-input]');
      var down = Math.max(0, parseInt(dnIn.value, 10) - 1);
      dnIn.value = down;
      changeLine(li, down);
      return;
    }
    if (e.target.closest('[data-cd-remove]') && li) {
      changeLine(li, 0);
      return;
    }

    var recBtn = e.target.closest('[data-cd-rec-add]');
    if (recBtn) {
      add(parseInt(recBtn.getAttribute('data-cd-rec-add'), 10), 1, recBtn);
      return;
    }

    if (e.target.closest('[data-cd-discount-apply]')) {
      var field = root.querySelector('[data-cd-discount]');
      var code = field && field.value.trim();
      if (!code) { if (field) field.focus(); return; }
      window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(window.location.pathname);
    }
  });

  root.addEventListener('change', function (e) {
    var li = e.target.closest('[data-cd-line]');
    if (li && e.target.matches('[data-cd-input]')) {
      var q = parseInt(e.target.value, 10);
      if (isNaN(q) || q < 0) q = 0;
      changeLine(li, q);
    }
  });

  root.addEventListener('input', function (e) {
    if (e.target.matches('[data-cd-note]')) updateNote(e.target.value);
  });

  root.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' && e.target.matches('[data-cd-discount]')) {
      e.preventDefault();
      var go = root.querySelector('[data-cd-discount-apply]');
      if (go) go.click();
    }
  });

  /* ---------- global triggers ------------------------------------------- */

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-bsfn-cart-open], .bh-cart');
    if (!trigger) return;
    // a modified click means "open the cart page in a new tab" — respect it
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    open();
  });

  /* An add that happened elsewhere (bsfn-line.js bindCart, a bundle section):
     pull fresh markup, then open if the merchant asked for that. */
  document.addEventListener('bsfn:cart:updated', function (e) {
    var d = e.detail || {};
    if (d.source === 'drawer') return;   // we already rendered it
    if (d.source === 'switch') {          // a line moved onto a selling plan inside the open drawer
      refresh().then(function () { status('Switched to a subscription'); });
      return;
    }
    refresh().then(function () {
      // An agent's quantity change or removal (bsfn-agent.js) re-renders in
      // place; only an add opens the drawer, as a shopper's own add does.
      if (d.source === 'agent' && d.action !== 'add') { status('Cart updated'); return; }
      if (!isOpen && openOnAdd) open();
    });
  });

  subDates();

  BSFN.cart = { open: open, close: close, refresh: refresh, add: add, isOpen: function () { return isOpen; } };
})();
